import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './KeycloakContext';

const CreditContext = createContext();

/**
 * Credit Context Provider
 * Maintains a live-updated credit balance.
 *
 * The actual Socket.io connection lives in App.js (single connection).
 * When App.js receives a credit-update from the server, it dispatches a
 * window CustomEvent which this context listens for — keeping every
 * component in sync without a second socket and without a page refresh.
 */
export function CreditProvider({ children }) {
  const { authenticated } = useAuth();
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditLimit, setCreditLimit] = useState(null);

  // Fetch initial balance via REST
  const fetchBalance = useCallback(async () => {
    if (!authenticated) return;
    try {
      const axios = (await import('../api/axios')).default;
      const res = await axios.get('/profile');
      if (res.data.success && res.data.data) {
        setCreditBalance(res.data.data.credit_balance ?? 0);
        setCreditLimit(res.data.data.credit_limit ?? 0);
      }
    } catch (err) {
      console.error('[CreditContext] Failed to fetch balance:', err);
    }
  }, [authenticated]);

  // Fetch on mount / auth change
  useEffect(() => {
    if (authenticated) {
      fetchBalance();
    } else {
      setCreditBalance(null);
      setCreditLimit(null);
    }
  }, [authenticated, fetchBalance]);

  // Listen for credit-update events dispatched by App.js's socket handler
  useEffect(() => {
    const handleCreditUpdate = (e) => {
      const data = e.detail;
      // Skip personal balance updates for org-pool-only balance changes
      // (e.g. an org admin using credits — the deduction hits the org
      // owner's pool, so the admin's personal balance must not be
      // overwritten with the pool balance).
      if (data?.isOrgPoolBalance) return;
      if (data?.credit_balance !== undefined) {
        setCreditBalance(data.credit_balance);
      }
      if (data?.credit_limit !== undefined) {
        setCreditLimit(data.credit_limit);
      }
    };
    window.addEventListener('credit-update', handleCreditUpdate);
    return () => window.removeEventListener('credit-update', handleCreditUpdate);
  }, []);

  // Derive usage status from balance + limit
  const getCreditStatus = useCallback(() => {
    const balance = creditBalance ?? 0;
    const limit = creditLimit ?? 0;
    if (limit > 0) {
      const usedPct = ((limit - balance) / limit) * 100;
      if (usedPct >= 100) return 'limited';
      if (usedPct >= 70) return 'warning';
      return 'available';
    }
    if (balance <= 0) return 'limited';
    if (balance <= 5) return 'warning';
    return 'available';
  }, [creditBalance, creditLimit]);

  const value = {
    creditBalance,
    creditLimit,
    creditStatus: getCreditStatus(),
    refreshBalance: fetchBalance,
  };

  return <CreditContext.Provider value={value}>{children}</CreditContext.Provider>;
}

/**
 * Hook to access live credit context
 */
export function useCredit() {
  const context = useContext(CreditContext);
  if (!context) {
    throw new Error('useCredit must be used within a CreditProvider');
  }
  return context;
}

export default CreditContext;